import React, { forwardRef, useCallback, useEffect, useRef, useState } from "react";
import { Badge, Box, ButtonBase, Dialog, IconButton, Slide, Tooltip, Typography, useMediaQuery } from "@mui/material";
import type { TransitionProps } from "@mui/material/transitions";
import { AssignmentIndOutlined, CalendarMonthOutlined, Close, ForumOutlined, MenuBookOutlined, OpenInNew } from "@mui/icons-material";
import AgendaAssistant from "./AgendaAssistant";
import CashBookAssistant from "./CashBookAssistant";
import PatientAssistant from "./PatientAssistant";
import { assistantsService, MyAssistantArea } from "../services/assistantsService";
import { useAuth } from "../contexts/AuthContext";
import { useRouter } from "../contexts/RouterContext";
import { colors } from "../theme/designSystem";
import { OPEN_ASSISTANTS_EVENT, setAssistantsOpen, setChatDock } from "../components/chat/chatDock";
import { CHAT_UNREAD_EVENT, chatUnreadStore } from "../services/chatService";
import brandIcon from "../images/favicon_clinic4us.png";

// App Assistentes (item 228, F1c — D-OP-16/17/19): modal em tela cheia por cima de qualquer página, como
// um app à parte (decisão do Hilton, 27/10). Menu de áreas à esquerda (barra de abas embaixo no celular).
// Cada área aparece só para quem abre a tela equivalente; a permissão continua checada nas rotas de cada
// área. Abrir põe `app=assistentes` no endereço: o voltar do navegador fecha o app e recarregar reabre.

type AreaKey = "conversas" | "prontuario" | "agenda" | "livro-caixa";

interface AreaDef {
  key: AreaKey;
  label: string;
  page: string; // página cuja permissão libera a área
  icon: React.ReactNode;
}

const AREAS: AreaDef[] = [
  { key: "conversas", label: "Conversas", page: "chat", icon: <ForumOutlined /> },
  { key: "prontuario", label: "Prontuário", page: "patient-assistant", icon: <AssignmentIndOutlined /> },
  // Agenda: além da tela, precisa do agente de consulta da área liberado para a pessoa (RN-565/567)
  { key: "agenda", label: "Agenda", page: "schedule", icon: <CalendarMonthOutlined /> },
  // Livro-caixa: a tela do caixa + o agente de consulta da área liberado (RN-571, D-OP-29)
  { key: "livro-caixa", label: "Livro-caixa", page: "cash-book", icon: <MenuBookOutlined /> },
];

const APP_PARAM = "app";
const APP_VALUE = "assistentes";

const params = () => new URLSearchParams(window.location.search);
const appInUrl = () => params().get(APP_PARAM) === APP_VALUE;
// Janela separada ("destacar"): a janela inteira é o app; fechar fecha a janela
export const isAssistantsPopout = () => params().get("popout") === "1";
const POPOUT_WINDOW = "clinic4us-assistentes";
const replaceParams = (p: URLSearchParams) =>
  window.history.replaceState(window.history.state, "", `${window.location.pathname}?${p.toString()}`);
const stripAppParams = () => {
  const p = params();
  p.delete(APP_PARAM);
  p.delete("area");
  replaceParams(p);
};

const SlideUp = forwardRef(function SlideUp(
  props: TransitionProps & { children: React.ReactElement },
  ref: React.Ref<unknown>
) {
  return <Slide direction="up" ref={ref} {...props} />;
});

// A área Conversas é o próprio chat do FAB, desenhado aqui pela mesma instância (chatDock).
const ConversationsArea: React.FC<{ active: boolean }> = ({ active }) => {
  const dockRef = useRef<HTMLDivElement | null>(null);

  // Só encaixa com a área à vista: escondida, a conversa aberta não pode ser dada como lida
  useEffect(() => {
    if (!active) return;
    setChatDock(dockRef.current);
    return () => setChatDock(null);
  }, [active]);

  return (
    <Box sx={{ flex: 1, minHeight: 0, display: "flex" }}>
      <Box ref={dockRef} sx={{ flex: 1, minWidth: 0, minHeight: 0, bgcolor: colors.white, overflow: "hidden" }} />
    </Box>
  );
};

const AssistantsApp: React.FC<{
  visible: boolean;
  areas: AreaDef[];
  agenda: MyAssistantArea | null;
  cashBook: MyAssistantArea | null;
  onClose: () => void;
  onPopout?: () => void;
}> = ({ visible, areas, agenda, cashBook, onClose, onPopout }) => {
  const [agendaPending, setAgendaPending] = useState(0);
  const bottomBar = useMediaQuery("(max-width:767.98px)");
  const pick = useCallback(
    (key: string | null): AreaKey | null => (areas.find((a) => a.key === key) ?? areas[0])?.key ?? null,
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [areas.map((a) => a.key).join(",")]
  );

  const [active, setActive] = useState<AreaKey | null>(() => pick(params().get("area")));
  // Área já aberta continua montada (escondida): conversa, paciente e rascunho sobrevivem à troca
  const [visited, setVisited] = useState<Set<AreaKey>>(() => new Set(active ? [active] : []));
  const [unread, setUnread] = useState<number>(chatUnreadStore.total);
  const tabRefs = useRef<Record<string, HTMLButtonElement | null>>({});

  // Reaberto por um pedido de área específica (openAssistants("prontuario")) ou com o perfil trocado
  useEffect(() => {
    if (visible) setActive((cur) => pick(params().get("area") ?? cur));
  }, [visible, pick]);

  useEffect(() => {
    if (active) setVisited((v) => (v.has(active) ? v : new Set(v).add(active)));
  }, [active]);

  useEffect(() => {
    const onUnread = (e: Event) => setUnread((e as CustomEvent<{ total: number }>).detail?.total ?? 0);
    window.addEventListener(CHAT_UNREAD_EVENT, onUnread);
    return () => window.removeEventListener(CHAT_UNREAD_EVENT, onUnread);
  }, []);

  // Trocar de área não empilha histórico: no app, o voltar fecha o app
  const select = (key: AreaKey) => {
    if (key === active) return;
    const p = params();
    p.set("area", key);
    replaceParams(p);
    setActive(key);
  };

  // Setas movem entre as áreas (vertical no desktop, horizontal na barra de baixo)
  const onKeyDown = (e: React.KeyboardEvent) => {
    const prevKey = bottomBar ? "ArrowLeft" : "ArrowUp";
    const nextKey = bottomBar ? "ArrowRight" : "ArrowDown";
    if (e.key !== prevKey && e.key !== nextKey && e.key !== "Home" && e.key !== "End") return;
    e.preventDefault();
    const i = areas.findIndex((a) => a.key === active);
    const target =
      e.key === "Home" ? 0 : e.key === "End" ? areas.length - 1 : (i + (e.key === nextKey ? 1 : -1) + areas.length) % areas.length;
    const key = areas[target].key;
    select(key);
    tabRefs.current[key]?.focus();
  };

  const showNav = areas.length > 1;
  const activeLabel = areas.find((a) => a.key === active)?.label;

  const appBar = (
    <Box
      sx={{
        display: "flex",
        alignItems: "center",
        gap: 1,
        flexShrink: 0,
        minHeight: 48,
        px: 0.5,
        pt: "env(safe-area-inset-top)",
        bgcolor: colors.white,
        borderBottom: `1px solid ${colors.backgroundAlt}`,
      }}
    >
      <IconButton onClick={onClose} aria-label="Fechar Super Assistente" sx={{ width: 44, height: 44, color: colors.textSecondary }}>
        <Close />
      </IconButton>
      <Typography component="h1" sx={{ fontSize: "1rem", fontWeight: 600, color: colors.textPrimary, flex: 1, minWidth: 0 }} noWrap>
        Super Assistente
        {activeLabel && showNav && (
          <Box component="span" sx={{ fontWeight: 400, color: colors.textSecondary }}>
            {" · "}
            {activeLabel}
          </Box>
        )}
      </Typography>
      {/* Janela própria do navegador: dá para levar a outro monitor (só desktop; no celular abriria outra aba) */}
      {onPopout && !bottomBar && (
        <Tooltip title="Abrir em janela separada">
          <IconButton onClick={onPopout} aria-label="Abrir em janela separada" sx={{ width: 44, height: 44, color: colors.textSecondary }}>
            <OpenInNew />
          </IconButton>
        </Tooltip>
      )}
    </Box>
  );

  const nav = showNav && (
    <Box
      role="tablist"
      aria-label="Áreas dos assistentes"
      aria-orientation={bottomBar ? "horizontal" : "vertical"}
      onKeyDown={onKeyDown}
      sx={
        bottomBar
          ? { display: "flex", flexShrink: 0, bgcolor: colors.white, borderTop: `1px solid ${colors.backgroundAlt}`, pb: "env(safe-area-inset-bottom)" }
          : { display: "flex", flexDirection: "column" }
      }
    >
      {areas.map((a) => {
        const selected = a.key === active;
        const count = a.key === "conversas" ? unread : a.key === "agenda" ? agendaPending : 0;
        return (
          <ButtonBase
            key={a.key}
            ref={(el) => {
              tabRefs.current[a.key] = el;
            }}
            role="tab"
            id={`assistants-tab-${a.key}`}
            aria-selected={selected}
            aria-controls={`assistants-area-${a.key}`}
            tabIndex={selected ? 0 : -1}
            onClick={() => select(a.key)}
            sx={{
              flexDirection: "column",
              gap: 0.5,
              minHeight: bottomBar ? 56 : 64,
              flex: bottomBar ? 1 : "none",
              px: 0.5,
              color: selected ? colors.primaryText : colors.textSecondary,
              bgcolor: selected ? colors.primaryLight : "transparent",
              // Mesma marca de seleção da lista do Prontuário: barra ciano interna (em cima, na barra de baixo)
              boxShadow: selected ? (bottomBar ? `inset 0 3px 0 ${colors.primary}` : `inset 3px 0 0 ${colors.primary}`) : "none",
              transition: "background-color 150ms ease-out, color 150ms ease-out",
              "&:hover": { bgcolor: colors.primaryLight, color: colors.primaryText },
              "&.Mui-focusVisible": { outline: `2px solid ${colors.primary}`, outlineOffset: -2 },
            }}
          >
            <Badge
              badgeContent={count}
              color="error"
              max={99}
              sx={{ "& .MuiBadge-badge": { fontWeight: 700, border: `2px solid ${colors.white}` } }}
            >
              <Box sx={{ display: "flex", color: selected ? colors.primary : "inherit", "& svg": { fontSize: 24 } }}>{a.icon}</Box>
            </Badge>
            <Typography component="span" sx={{ fontSize: "0.75rem", fontWeight: selected ? 600 : 500, lineHeight: 1.2 }}>
              {a.label}
            </Typography>
          </ButtonBase>
        );
      })}
    </Box>
  );

  const content = (
    <Box sx={{ flex: 1, minWidth: 0, minHeight: 0, display: "flex", flexDirection: "column" }}>
      {areas.length === 0 && (
        <Box sx={{ flex: 1, display: "flex", alignItems: "center", justifyContent: "center", p: 3 }}>
          <Typography sx={{ color: colors.textSecondary, fontSize: "0.95rem", textAlign: "center", maxWidth: 420 }}>
            Nenhum assistente está liberado para o seu perfil. Fale com o administrador da clínica.
          </Typography>
        </Box>
      )}
      {areas.map(
        (a) =>
          visited.has(a.key) && (
            <Box
              key={a.key}
              role={showNav ? "tabpanel" : undefined}
              id={`assistants-area-${a.key}`}
              aria-labelledby={showNav ? `assistants-tab-${a.key}` : undefined}
              hidden={a.key !== active}
              sx={{ flex: 1, minHeight: 0, display: a.key === active ? "flex" : "none", flexDirection: "column" }}
            >
              {a.key === "conversas" && <ConversationsArea active={visible && a.key === active} />}
              {a.key === "prontuario" && <PatientAssistant embedded />}
              {a.key === "agenda" && agenda && <AgendaAssistant area={agenda} onPendingChange={setAgendaPending} />}
              {a.key === "livro-caixa" && cashBook && <CashBookAssistant area={cashBook} />}
            </Box>
          )
      )}
    </Box>
  );

  return (
    <Box sx={{ display: "flex", flexDirection: "column", height: "100%", bgcolor: colors.background }}>
      {appBar}
      <Box component="main" sx={{ flex: 1, minHeight: 0, display: "flex", flexDirection: bottomBar ? "column" : "row" }}>
        {!bottomBar && showNav && (
          <Box sx={{ display: "flex", flexDirection: "column", flexShrink: 0, width: 76, bgcolor: colors.white, borderRight: `1px solid ${colors.backgroundAlt}` }}>
            {/* Marca no topo do trilho: o "c4" no modelo da logo, como ícone de app */}
            <Box sx={{ height: 64, display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0 }}>
              <Box component="img" src={brandIcon} alt="Clinic4Us" sx={{ width: 36, height: 36, objectFit: "contain" }} />
            </Box>
            {nav}
          </Box>
        )}
        {content}
        {bottomBar && nav}
      </Box>
    </Box>
  );
};

// Montado uma vez na raiz do app, ao lado do chat. Abre pelo menu (evento), pelo endereço
// (`app=assistentes`, ao recarregar) ou pelo link antigo `?page=assistants` (abre sobre o Dashboard).
const AssistantsHost: React.FC = () => {
  const { user, isAuthenticated } = useAuth();
  const { currentPage, navigateTo } = useRouter();
  const allowed = user?.allowedPages || [];
  const canUse = isAuthenticated && allowed.includes("assistants");
  const [myAreas, setMyAreas] = useState<MyAssistantArea[]>([]);
  const agenda = myAreas.find((a) => a.areaKey === "agenda") ?? null;
  const cashBook = myAreas.find((a) => a.areaKey === "livro-caixa") ?? null;
  const areas = AREAS.filter((a) => allowed.includes(a.page)
    && (a.key !== "agenda" || !!agenda) && (a.key !== "livro-caixa" || !!cashBook));

  // Áreas com agente (Agenda): dependem da configuração da clínica e da liberação por pessoa
  useEffect(() => {
    if (!canUse) {
      setMyAreas([]);
      return;
    }
    let alive = true;
    assistantsService.myAreas().then((r) => { if (alive) setMyAreas(r); }).catch(() => { if (alive) setMyAreas([]); });
    return () => { alive = false; };
  }, [canUse, user?.userId, user?.profileId]);

  const [open, setOpen] = useState(false);
  // Depois da primeira abertura fica montado: fechar e reabrir não perde a conversa nem o paciente
  const [mounted, setMounted] = useState(false);
  const pushedRef = useRef(false);
  const pageRef = useRef(currentPage);
  const titleRef = useRef<string | null>(null);

  useEffect(() => {
    if (canUse && (appInUrl() || currentPage === "assistants")) setOpen(true);
  }, [canUse, currentPage]);

  // Navegar para outra tela de dentro do app (ex.: "Abrir cadastro" no chat) fecha o app
  useEffect(() => {
    const previous = pageRef.current;
    pageRef.current = currentPage;
    if (open && previous !== currentPage && currentPage !== "assistants") {
      pushedRef.current = false;
      stripAppParams();
      setOpen(false);
    }
  }, [currentPage, open]);

  useEffect(() => {
    const onOpen = (e: Event) => {
      if (!canUse) return;
      const area = (e as CustomEvent<{ area?: string }>).detail?.area;
      if (!appInUrl()) {
        const p = params();
        p.set(APP_PARAM, APP_VALUE);
        if (area) p.set("area", area);
        window.history.pushState({}, "", `${window.location.pathname}?${p.toString()}`);
        pushedRef.current = true;
      }
      setOpen(true);
    };
    window.addEventListener(OPEN_ASSISTANTS_EVENT, onOpen);
    return () => window.removeEventListener(OPEN_ASSISTANTS_EVENT, onOpen);
  }, [canUse]);

  // Voltar do navegador/celular: sem o parâmetro no endereço, o app fecha
  useEffect(() => {
    const onPop = () => {
      if (!appInUrl() && pageRef.current !== "assistants") {
        pushedRef.current = false;
        setOpen(false);
      }
    };
    window.addEventListener("popstate", onPop);
    return () => window.removeEventListener("popstate", onPop);
  }, []);

  useEffect(() => {
    if (open) {
      setMounted(true);
      titleRef.current = document.title;
      document.title = "Clinic4Us - Super Assistente";
    } else if (titleRef.current !== null) {
      document.title = titleRef.current;
      titleRef.current = null;
    }
    setAssistantsOpen(open);
  }, [open]);

  useEffect(() => () => setAssistantsOpen(false), []);

  const popout = isAssistantsPopout();

  // Abre o app numa janela própria (mesmo login, dados iguais) e tira o modal desta janela
  const openPopout = () => {
    const p = new URLSearchParams();
    p.set("page", "assistants");
    p.set("popout", "1");
    const area = params().get("area");
    if (area) p.set("area", area);
    const w = window.open(`${window.location.pathname}?${p.toString()}`, POPOUT_WINDOW, "popup,width=1200,height=820");
    if (w) {
      w.focus();
      close();
    }
  };

  const close = () => {
    if (popout) {
      window.close();
      return;
    }
    if (pageRef.current === "assistants") {
      stripAppParams();
      setOpen(false);
      navigateTo("dashboard");
    } else if (pushedRef.current) {
      window.history.back();
    } else {
      stripAppParams();
      setOpen(false);
    }
  };

  if (!canUse || (!open && !mounted)) return null;

  return (
    <Dialog
      open={open}
      onClose={close}
      fullScreen
      keepMounted
      TransitionComponent={SlideUp}
      aria-label="Super Assistente"
      // A regra global de modal no celular (global.css, RN-539f) põe 12px de margem com !important em
      // todo Dialog; o app é tela cheia de verdade, então anula a margem com especificidade maior.
      sx={{
        "& .MuiDialog-paper.MuiDialog-paperFullScreen": {
          m: "0 !important",
          width: "100% !important",
          maxWidth: "100% !important",
          maxHeight: "100% !important",
          height: "100%",
          borderRadius: 0,
        },
      }}
      PaperProps={{ sx: { bgcolor: colors.background } }}
    >
      <AssistantsApp visible={open} areas={areas} agenda={agenda} cashBook={cashBook} onClose={close} onPopout={popout ? undefined : openPopout} />
    </Dialog>
  );
};

export default AssistantsHost;
